import { useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";

import { useServiceCallViewModel } from "../../../viewmodels/operation/servicecall/useServiceCallViewModel";

import ScaleMakeAsyncSelect from "../../../components/common/ScaleMakeAsyncSelect";

import ScaleModelAsyncSelect from "../../../components/common/ScaleModelAsyncSelect";

import DashboardLayout from "../../../components/layout/DashboardLayout";

import { useCustomerViewModel } from "../../../viewmodels/operation/customer/useCustomerViewModel";

import { useCalibrationCertificateViewModel } from "../../../viewmodels/operation/calibrationcertificate/useCalibrationCertificateViewModel";

const CreateCalibrationCertificate = () => {
  const [customerSelected, setCustomerSelected] = useState(false);

  const {
    serviceCalls,
    setSearch: setServiceCallSearch,
    getServiceCallById,
  } = useServiceCallViewModel();

  const [selectedServiceCallId, setSelectedServiceCallId] = useState("");

  const [selectedMake, setSelectedMake] = useState<any>(null);
  const [selectedModel, setSelectedModel] = useState<any>(null);

  const navigate = useNavigate();

  const { createCalibrationCertificate } = useCalibrationCertificateViewModel();

  const { customers, setSearch } = useCustomerViewModel();

  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  const customerDropdownRef = useRef<any>(null);

  const [form, setForm] = useState<any>({
    no: "",
    date: "",

    customerName: "",
    customerEmail: "",
    scaleLocation: "",
    contactPerson: "",
    contactPhoneNumber: "",
    customerAddress: "",

    reportNo: "",

    make: "",
    model: "",
    serialNumber: "",
    capacity: "",
    scaleId: null,

    nmiNumber: "",

    beforeCalibration: 0, // Pass
    afterCalibration: 0, // Pass

    comments: "",

    frequency: "",

    maximumPermissibleError: "",

    verificationCode: "",

    scaleCertified: 1, // No
    scaleUsedForTrade: 1, // No

    verificationDate: "",
    calibratedBy: "",
    calibratedDate: "",
    dueDate: "",
    serviceCallId: "",
    identifier: "",
    isCalibrationCertificate: true,
  });

  useEffect(() => {
    if (form.capacityValue && form.capacityUnit && form.division) {
      generateCalibrationRows(Number(form.capacityValue), form.capacityUnit);
    }
  }, [form.capacityValue, form.capacityUnit, form.division]);

  const [lineItems, setLineItems] = useState<any[]>([]);

  // ================= OUTSIDE CLICK =================
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target)
      ) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ================= SELECT CUSTOMER =================
  const handleSelectCustomer = async (customer: any) => {
    const firstAddress = customer?.addresses?.[0];

    const fullAddress = [
      firstAddress?.addressLine1,
      firstAddress?.addressLine2,
      firstAddress?.city,
      firstAddress?.state,
      firstAddress?.postalCode,
    ]
      .filter(Boolean)
      .join(", ");

    const firstPhone = customer?.customerPhoneNumbers?.[0];

    setForm({
      ...form,

      customerName: customer.name || "",

      customerEmail: customer.email || "",

      contactPerson: firstPhone?.name || "",

      contactPhoneNumber: firstPhone?.phoneNumber || "",

      customerAddress: fullAddress,

      serviceCallId: "",
    });

    setShowCustomerDropdown(false);

    setSelectedServiceCallId("");

    setLineItems([]);

    setForm((prev: any) => ({
      ...prev,

      make: "",
      model: "",
      serialNumber: "",
      capacity: "",
      capacityValue: "",
      division: "",
      capacityUnit: "",
    }));

    setServiceCallSearch(customer.name);

    setCustomerSelected(true);

    setServiceCallSearch(customer.name);
  };

  // ================= ADD ROW =================
  const addRow = () => {
    setLineItems([
      ...lineItems,
      {
        lineNumber: lineItems.length + 1,
        loadApplied: "",
        beforeCalibration: "",
        beforeCalibrationError: "",
        afterCalibration: "",
        afterCalibrationError: "",
      },
    ]);
  };

  // ================= REMOVE ROW =================
  const removeRow = (index: number) => {
    const updated = [...lineItems];

    updated.splice(index, 1);

    setLineItems(updated);
  };

  // ================= SAVE =================
  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const decimalPlaces = getDecimalPlaces(form.division || "0");

    const unit = form.capacityUnit || "";

    const payload = {
      ...form,

      scaleId: form.scaleId ? form.scaleId : null,

      serviceCallId: form.serviceCallId || null,

      no: Number(form.no || 0),

      beforeCalibration: Number(form.beforeCalibration),

      afterCalibration: Number(form.afterCalibration),

      scaleCertified: Number(form.scaleCertified),

      scaleUsedForTrade: Number(form.scaleUsedForTrade),

      identifier: form.identifier,

      date: form.date ? new Date(form.date).toISOString() : null,

      verificationDate: form.verificationDate
        ? new Date(form.verificationDate).toISOString()
        : null,

      calibratedDate: form.calibratedDate
        ? new Date(form.calibratedDate).toISOString()
        : null,

      dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : null,

      lineItems: lineItems.map((x, index) => ({
        ...x,

        lineNumber: index + 1,

        loadApplied: `${getNumber(x.loadApplied).toFixed(decimalPlaces)}${unit}`,

        beforeCalibration: `${getNumber(x.beforeCalibration).toFixed(decimalPlaces)}${unit}`,

        beforeCalibrationError: `${getNumber(x.beforeCalibrationError).toFixed(decimalPlaces)}${unit}`,

        afterCalibration: `${getNumber(x.afterCalibration).toFixed(decimalPlaces)}${unit}`,

        afterCalibrationError: `${getNumber(x.afterCalibrationError).toFixed(decimalPlaces)}${unit}`,
      })),
    };

    const success = await createCalibrationCertificate(payload);

    if (success) {
      navigate("/calibration-certificates");
    }
  };

  const generateCalibrationRows = (capacityValue: number, unit: string) => {
    if (!capacityValue || !unit) return;

    const decimalPlaces = getDecimalPlaces(form.division || "0");

    const loads = [
      0,
      capacityValue * 0.25,
      capacityValue * 0.5,
      capacityValue * 0.75,
      capacityValue,
    ];

    setLineItems(
      loads.map((load, index) => ({
        lineNumber: index + 1,

        loadApplied: `${load.toFixed(decimalPlaces)}`,

        beforeCalibration: `${load.toFixed(decimalPlaces)}`,

        beforeCalibrationError: `${(0).toFixed(decimalPlaces)}`,

        afterCalibration: `${load.toFixed(decimalPlaces)}`,

        afterCalibrationError: `${(0).toFixed(decimalPlaces)}`,
      })),
    );
  };

  const parseCapacity = (capacity: string) => {
    if (!capacity)
      return {
        capacityValue: "",

        division: "",

        capacityUnit: "",
      };

    const match = capacity.match(/^([\d.]+)(kg|g|mg|t)x([\d.]+)(kg|g|mg|t)$/i);

    if (!match)
      return {
        capacityValue: "",

        division: "",

        capacityUnit: "",
      };

    return {
      capacityValue: match[1],

      capacityUnit: match[2],

      division: match[3],
    };
  };

  const getDecimalPlaces = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) {
      return 0;
    }

    const text = value.toString();

    if (!text.includes(".")) {
      return 0;
    }

    return text.split(".")[1].length;
  };

  const getNumber = (value: string) => {
    return parseFloat(String(value).replace(/[^\d.-]/g, "")) || 0;
  };

  useEffect(() => {
    if (!form.date || !form.frequency) return;

    const due = new Date(form.date);
    due.setMonth(due.getMonth() + Number(form.frequency));

    const dueDate = due.toISOString().split("T")[0];

    if (form.dueDate !== dueDate) {
      setForm((prev: any) => ({
        ...prev,
        dueDate,
      }));
    }
  }, [form.date, form.frequency]);

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">
          Create Calibration Certificate
        </h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* ================= CUSTOMER DETAILS ================= */}
          <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl font-bold mb-8">Customer Details</h2>

            <div className="grid grid-cols-2 md:grid-cols-2 gap-8 mt-4">
              <div className="relative" ref={customerDropdownRef}>
                <label className="block text-[15px] font-semibold mb-2">
                  Customer Name
                </label>

                <input
                  required
                  value={form.customerName}
                  onFocus={() => setShowCustomerDropdown(true)}
                  onChange={(e) => {
                    setForm({
                      ...form,

                      customerName: e.target.value,
                    });

                    setSearch(e.target.value);

                    setShowCustomerDropdown(true);
                  }}
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />

                {showCustomerDropdown && (
                  <div className="absolute z-50 bg-white border rounded-2xl shadow-xl w-full mt-2 max-h-72 overflow-auto">
                    <button
                      type="button"
                      onClick={() => setShowCustomerDropdown(false)}
                      className="w-full text-left px-4 py-4 bg-primary text-white font-semibold"
                    >
                      Manual Entry
                    </button>

                    {customers.map((x: any) => (
                      <button
                        key={x.id}
                        type="button"
                        onClick={() => handleSelectCustomer(x)}
                        className="w-full text-left px-4 py-4 border-b hover:bg-slate-100"
                      >
                        {x.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Service Call
                </label>
                <select
                  disabled={!customerSelected}
                  value={selectedServiceCallId}
                  onChange={async (e) => {
                    const id = e.target.value;

                    setSelectedServiceCallId(id);

                    if (!id) return;

                    const serviceCall = await getServiceCallById(id);

                    setForm((prev: any) => ({
                      ...prev,
                      serviceCallId: id,
                    }));

                    if (!serviceCall) return;

                    const scale = serviceCall?.serviceCallScales?.[0];

                    if (!scale) return;

                    const parsed = parseCapacity(scale.capacity);

                    setForm((prev: any) => ({
                      ...prev,

                      serviceCallId: id,

                      make: scale.make,
                      model: scale.model,
                      serialNumber: scale.serialNumber,

                      capacity: scale.capacity,

                      capacityValue: parsed.capacityValue,
                      division: parsed.division,
                      capacityUnit: parsed.capacityUnit,
                    }));

                    generateCalibrationRows(
                      Number(parsed.capacityValue),

                      parsed.capacityUnit,
                    );
                  }}
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                >
                  <option value="">Select Service Call</option>
                  {!customerSelected && (
                    <option value="">Select customer first</option>
                  )}

                  {customerSelected &&
                    serviceCalls.map((x: any) => (
                      <option key={x.id} value={x.id}>
                        {x.no}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-4">
              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Customer Email
                </label>

                <input
                  value={form.customerEmail}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      customerEmail: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>

              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Contact Person
                </label>

                <input
                  value={form.contactPerson}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      contactPerson: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>

              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Contact Phone Number
                </label>

                <input
                  value={form.contactPhoneNumber}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      contactPhoneNumber: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>
            </div>

            <div>
              <label className="block text-[15px] font-semibold mb-2 mt-4">
                Customer Address
              </label>

              <textarea
                rows={4}
                value={form.customerAddress}
                onChange={(e) =>
                  setForm({
                    ...form,
                    customerAddress: e.target.value,
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              />
            </div>
          </div>

          {/* ================= SCALE DETAILS ================= */}
          <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl font-bold mb-8">Scale Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Make
                </label>

                <ScaleMakeAsyncSelect
                  value={selectedMake}
                  onChange={(selected: any) => {
                    setSelectedMake(selected);
                    setSelectedModel(null);

                    setForm((prev: any) => ({
                      ...prev,
                      makeId: selected?.__isNew__ ? null : selected?.value,
                      make: selected?.label ?? "",

                      modelId: null,
                      model: "",
                    }));
                  }}
                />
              </div>

              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Model
                </label>

                <ScaleModelAsyncSelect
                  value={selectedModel}
                  onChange={(selected: any) => {
                    setSelectedModel(selected);

                    if (!selected) {
                      setForm((prev: any) => ({
                        ...prev,
                        modelId: null,
                        model: "",
                        capacityValue: "",
                        division: "",
                        capacityUnit: "kg",
                        capacity: "",
                      }));
                      return;
                    }

                    const modelCapacity = selected.capacities?.[0];

                    const capacityValue =
                      modelCapacity?.capacity?.toString() ?? "";

                    const division = modelCapacity?.division?.toString() ?? "";

                    const capacityUnit =
                      modelCapacity?.capacityUnitName ?? "kg";

                    const divisionUnit =
                      modelCapacity?.divisionUnitName ?? capacityUnit;

                    setForm((prev: any) => ({
                      ...prev,

                      modelId: selected.__isNew__ ? null : selected.value,
                      model: selected.label,

                      // Populate these fields first
                      capacityValue,
                      division,
                      capacityUnit,

                      // Then build the display string
                      capacity:
                        capacityValue && division
                          ? `${capacityValue}${capacityUnit}x${division}${divisionUnit}`
                          : "",
                    }));
                  }}
                />
              </div>
            </div>
            <div>
              <label className="block text-[15px] font-semibold mb-2 mt-4">
                Capacity
              </label>

              <div className="grid grid-cols-3 gap-3">
                {/* CAPACITY VALUE */}
                <input
                  type="number"
                  min="0"
                  placeholder="3000"
                  value={form.capacityValue || ""}
                  onChange={(e) => {
                    const value = Math.max(
                      0,
                      Number(e.target.value),
                    ).toString();

                    setForm({
                      ...form,

                      capacityValue: value,

                      capacity:
                        value && form.division && form.capacityUnit
                          ? `${value}${form.capacityUnit} x ${form.division}${form.capacityUnit}`
                          : "",
                    });
                  }}
                  className="border-2 border-slate-300 rounded-2xl px-5 py-4"
                />

                {/* DIVISION */}
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  placeholder="0.5"
                  value={form.division || ""}
                  onChange={(e) => {
                    const value = e.target.value;

                    // prevent negative values
                    if (value.startsWith("-")) return;

                    setForm({
                      ...form,

                      division: value,

                      capacity:
                        form.capacityValue && value && form.capacityUnit
                          ? `${form.capacityValue}${form.capacityUnit}x${value}${form.capacityUnit}`
                          : "",
                    });
                  }}
                  className="border-2 border-slate-300 rounded-2xl px-5 py-4"
                />

                {/* UNIT */}
                <select
                  value={form.capacityUnit || ""}
                  onChange={(e) => {
                    const unit = e.target.value;

                    const capacity =
                      form.capacityValue && form.division
                        ? `${form.capacityValue}${unit}x${form.division}${unit}`
                        : "";

                    setForm({
                      ...form,

                      capacityUnit: unit,

                      capacity,
                    });

                    if (form.capacityValue && Number(form.capacityValue) > 0) {
                    }
                  }}
                  className="border-2 border-slate-300 rounded-2xl px-5 py-4"
                >
                  <option value="">Unit</option>

                  <option value="kg">kg</option>

                  <option value="g">g</option>

                  <option value="mg">mg</option>

                  <option value="t">t</option>

                  <option value="lb">lb</option>

                  <option value="oz">oz</option>
                </select>
              </div>

              {/* PREVIEW */}
              {form.capacity && (
                <div className="mt-2 text-sm text-slate-600">
                  Capacity Sent:
                  <span className="font-semibold ml-2">{form.capacity}</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mt-4">
              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Serial Number
                </label>

                <input
                  value={form.serialNumber}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      serialNumber: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4 uppercase"
                />
              </div>

              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  NMI Number
                </label>

                <input
                  value={form.nmiNumber}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      nmiNumber: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>

              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Id
                </label>

                <input
                  value={form.identifier}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      identifier: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>

              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Scale Location
                </label>

                <input
                  value={form.scaleLocation}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      scaleLocation: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>
            </div>
          </div>

          {/* ================= LINE ITEMS ================= */}
          <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Line Items</h2>

              <button
                type="button"
                onClick={addRow}
                className="bg-primary text-white px-5 py-3 rounded-2xl"
              >
                + Add Row
              </button>
            </div>

            <table className="w-full border-2 border-slate-300">
              <thead className="bg-slate-100">
                <tr>
                  <th className="border p-3">Load Applied</th>

                  <th className="border p-3">Before Calibration</th>

                  <th className="border p-3">Error (+/-)</th>

                  <th className="border p-3">After Calibration</th>

                  <th className="border p-3">Error (+/-)</th>

                  <th className="border p-3">Action</th>
                </tr>
              </thead>

              <tbody>
                {lineItems.map((item, index) => (
                  <tr key={index}>
                    {[
                      "loadApplied",
                      "beforeCalibration",
                      "beforeCalibrationError",
                      "afterCalibration",
                      "afterCalibrationError",
                    ].map((field) => (
                      <td key={field} className="border p-2">
                        <input
                          value={item[field]}
                          onChange={(e) => {
                            const updated = [...lineItems];

                            updated[index][field] = e.target.value;

                            const decimalPlaces = getDecimalPlaces(
                              form.division || "0",
                            );

                            if (field === "loadApplied") {
                              const load = getNumber(e.target.value);

                              // Automatically copy Load Applied
                              updated[index].beforeCalibration =
                                load.toFixed(decimalPlaces);
                              updated[index].afterCalibration =
                                load.toFixed(decimalPlaces);
                            }

                            const load = getNumber(updated[index].loadApplied);
                            const before = getNumber(
                              updated[index].beforeCalibration,
                            );
                            const after = getNumber(
                              updated[index].afterCalibration,
                            );

                            updated[index].beforeCalibrationError = (
                              before - load
                            ).toFixed(decimalPlaces);

                            updated[index].afterCalibrationError = (
                              after - load
                            ).toFixed(decimalPlaces);

                            setLineItems(updated);
                          }}
                          className="w-full border rounded-xl px-3 py-2"
                        />
                      </td>
                    ))}

                    <td className="border p-2 text-center">
                      <button
                        type="button"
                        onClick={() => removeRow(index)}
                        className="bg-red-500 text-white px-4 py-2 rounded-xl"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ================= CERTIFICATION ================= */}
          <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Before Calibration
                </label>

                <select
                  value={form.beforeCalibration}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      beforeCalibration: Number(e.target.value),
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                >
                  <option value={0}>Pass</option>
                  <option value={1}>Fail</option>
                </select>
              </div>

              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  After Calibration
                </label>

                <select
                  value={form.afterCalibration}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      afterCalibration: Number(e.target.value),
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                >
                  <option value={0}>Pass</option>
                  <option value={1}>Fail</option>
                </select>
              </div>
            </div>
          </div>

          {/* ================= COMMENTS ================= */}
          <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl font-bold mb-8">Comments</h2>

            <textarea
              rows={6}
              value={form.comments}
              onChange={(e) =>
                setForm({
                  ...form,
                  comments: e.target.value,
                })
              }
              className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
            />
          </div>

          {/* ================= CALIBRATION DETAILS ================= */}
          <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl font-bold mb-8">Calibration Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mt-5">
              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Frequency(Months)
                </label>

                <input
                  type="number"
                  min="0"
                  value={form.frequency}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      frequency: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>
              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Max. Perm. Error
                </label>

                <input
                  value={form.maximumPermissibleError}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      maximumPermissibleError: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>
              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Verification Code
                </label>

                <input
                  value={form.verificationCode}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      verificationCode: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>
              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Scale Certified
                </label>

                <select
                  value={form.scaleCertified}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      scaleCertified: Number(e.target.value),
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                >
                  <option value={0}>Yes</option>
                  <option value={1}>No</option>
                </select>
              </div>
              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Scale Used For Trade
                </label>

                <select
                  value={form.scaleUsedForTrade}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      scaleUsedForTrade: Number(e.target.value),
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                >
                  <option value={0}>Yes</option>
                  <option value={1}>No</option>
                </select>
              </div>

              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Verification Date
                </label>

                <input
                  type="date"
                  value={form.verificationDate}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      verificationDate: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>
              {/* <div>
                <label className="block font-semibold mb-2">
                  Calibrated Date
                </label>

                <input
                  type="date"
                  value={form.calibratedDate}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      calibratedDate: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>
 */}
              <div>
                <label className="block font-semibold mb-2">
                  Calibrated By
                </label>

                <input
                  value={form.calibratedBy}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      calibratedBy: e.target.value.toUpperCase(),
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>

              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Date
                </label>

                <input
                  type="date"
                  value={form.date}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      date: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>
              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Due Date
                </label>

                <input
                  type="date"
                  value={form.dueDate}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      dueDate: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>
            </div>
          </div>

          {/* SAVE */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="px-8 py-3 rounded-2xl bg-primary text-white font-semibold"
            >
              Save Calibration Certificate
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default CreateCalibrationCertificate;
